<template>
  <v-container fluid class="pa-0">
    <v-card outlined>
      <v-card-text class="pa-4">
        <!-- FILTERS SECTION -->
        <v-form ref="reportForm">
          <v-row align="center">
            <v-col cols="12" sm="4">
              <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="report.parameters.startDate" label="Start Date" prepend-icon="mdi-calendar"
                    readonly outlined dense hide-details v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="report.parameters.startDate" @input="startDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="4">
              <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="report.parameters.endDate" label="End Date" prepend-icon="mdi-calendar"
                    readonly outlined dense hide-details v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="report.parameters.endDate" @input="endDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="4" class="text-right">
              <v-btn color="primary" @click="generatePreview" :loading="isGeneratingPreview" large>
                <v-icon left>mdi-eye</v-icon>
                Generate Preview
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <!-- RESULTS SECTION -->
        <template v-if="groupedData && groupedData.length > 0">
          <v-divider class="my-6"></v-divider>

          <!-- CONTROLS BAR -->
          <v-row align="center" class="mb-4">
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedUnit"
                :items="unitOptions"
                label="Filter by Unit"
                outlined
                dense
                clearable
                prepend-icon="mdi-filter"
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn-toggle v-model="sortMode" mandatory dense>
                <v-btn small value="reference">Sort by Reference</v-btn>
                <v-btn small value="unit">Sort by Unit</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
              <v-btn color="red" dark @click="generatePdf" :loading="generatingPdf">
                <v-icon left>mdi-file-pdf-box</v-icon>
                Export PDF
              </v-btn>
            </v-col>
          </v-row>

          <!-- REPORT CONTENT -->
          <div id="print-content" class="pdf-content mt-4">
            <!-- HEADER -->
            <div class="report-header text-center mb-6">
              <img v-if="base64log" :src="logoSrc" style="max-height: 120px; max-width: 120px;" />
              <h4 class="mt-3 mb-2">{{ report.title }}</h4>
              <p class="mb-1" v-if="isLandlordMode"><strong>Landlord:</strong> {{ landlordName }}</p>
              <p class="mb-1"><strong>{{ isLandlordMode ? 'Properties' : 'Property' }}:</strong> {{ headerPropertyText }}</p>
              <p class="mb-0"><strong>Period:</strong> {{ formatDate(report.parameters.startDate) }} to {{ formatDate(report.parameters.endDate) }}</p>
              <v-divider class="my-3"></v-divider>
            </div>

            <!-- LEASE CARDS -->
            <div v-for="(group, index) in displayedGroupedData" :key="index" class="lease-card mb-6">
              <!-- Lease Header -->
              <div class="lease-header grey lighten-4 py-2 px-3">
                <div class="lease-header-row">
                  <span class="header-item"><strong>Ref:</strong> {{ group.reference }}</span>
                  <span class="header-item"><strong>Tenant:</strong> {{ group.tenantName || 'Unknown Tenant' }}</span>
                  <span class="header-item"><strong>Property:</strong> {{ group.property }}</span>
                  <span class="header-item" v-if="group.unit"><strong>Unit:</strong> {{ group.unit }}</span>
                </div>
              </div>

              <!-- Table -->
              <div class="table-wrapper">
                <table class="rent-roll-table">
                  <thead>
                    <tr>
                      <th v-for="col in visibleMonthColumns" :key="col.field" class="text-center">{{ col.header }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in group.items" :key="idx" :class="{ 'payment-row': item.isPayment }">
                      <td v-for="col in visibleMonthColumns" :key="col.field" class="text-center">
                        <template v-if="col.field === 'month'">
                          <span v-if="!item.isPayment">{{ item.month }}</span>
                          <span v-else class="payment-indicator">↳ Payment</span>
                        </template>
                        <template v-else-if="col.field === 'balance'">
                          <span :class="getBalanceClass(item)">{{ formatCurrency(item.balance) }}</span>
                        </template>
                        <template v-else-if="chargeColumns.includes(col.field)">
                          <span :class="getChargeStatusClass(item, col.field)">
                            {{ formatCurrency(item[col.field] || 0) }}
                          </span>
                        </template>
                        <template v-else-if="col.field === 'paymentDate'">
                          <span class="payment-date">{{ item.paymentDate }}</span>
                        </template>
                        <template v-else>
                          {{ col.formatter ? col.formatter(item[col.field]) : item[col.field] }}
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- LEGEND -->
            <div class="report-legend mt-6">
              <div class="text-subtitle-2 font-weight-bold mb-3">Legend</div>
              <div class="legend-row">
                <span class="legend-item">
                  <span class="legend-square success-bg mr-2"></span>
                  <span>Paid up (balance ≤ 0)</span>
                </span>
                <span class="legend-item">
                  <span class="legend-square orange-bg mr-2"></span>
                  <span>Partially paid</span>
                </span>
                <span class="legend-item">
                  <span class="legend-square error-bg mr-2"></span>
                  <span>Owing (balance &gt; 0)</span>
                </span>
                <span class="legend-item">
                  <span class="legend-square payment-bg mr-2" style="background: #e3f2fd; border: 1px dashed #1976d2;"></span>
                  <span>Payment row</span>
                </span>
              </div>
            </div>
          </div>
        </template>

        <!-- NO DATA -->
        <div v-else-if="groupedData && groupedData.length === 0" class="text-center py-8">
          <v-icon large color="grey">mdi-file-document-outline</v-icon>
          <p class="grey--text text--darken-1 mt-3">No leases found for the selected criteria.</p>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const db = getFirestore();

export default {
  name: "RentRollReport",
  props: {
    property: { type: Object, default: null },
    properties: { type: Array, default: () => [] },
    unit: { type: Object, default: null },
    landlord: { type: Object, default: null }
  },
  data() {
    const today = new Date();
    const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, 1);
    const lastDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      propertyName: '',
      landlordName: '',
      report: {
        title: 'Rent Roll Report',
        parameters: {
          startDate: sixMonthsAgo.toISOString().split('T')[0],
          endDate: lastDayOfCurrentMonth.toISOString().split('T')[0],
        },
      },
      startDateMenu: false,
      endDateMenu: false,
      isGeneratingPreview: false,
      generatingPdf: false,
      groupedData: [],
      base64log: "",
      sortMode: 'unit',
      selectedUnit: null,
      unitOptions: [{ text: 'All Units', value: null }],
      chargeColumns: ['rent','water','sewerage','sewerageAv','refuse','admin','parking','insurance','legalExpenses','repairsMaintenance','utilities','otherVendorExpenses','otherCharge'],
      monthColumnDefinitions: [
        { field: 'month', header: 'Month', alwaysVisible: true },
        { field: 'bcForward', header: 'B/C Fwd', isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { field: 'rent', header: 'Rent', alwaysVisible: true },
        { field: 'water', header: 'Water', alwaysVisible: true },
        { field: 'sewerage', header: 'Sewerage', alwaysVisible: true },
        { field: 'sewerageAv', header: 'Sew Av', alwaysVisible: true },
        { field: 'refuse', header: 'Refuse', alwaysVisible: true },
        { field: 'admin', header: 'Admin' },
        { field: 'parking', header: 'Parking' },
        { field: 'insurance', header: 'Insurance' },
        { field: 'legalExpenses', header: 'Legal Exp' },
        { field: 'repairsMaintenance', header: 'Repairs' },
        { field: 'utilities', header: 'Utilities' },
        { field: 'otherVendorExpenses', header: 'Vendor Exp' },
        { field: 'otherCharge', header: 'Other' },
        { field: 'subTotal', header: 'Sub Total', alwaysVisible: true, isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { field: 'paymentDate', header: 'Payment Date', alwaysVisible: true },
        { field: 'bankReceived', header: 'Bank Recv', alwaysVisible: true, isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { field: 'balance', header: 'Balance', alwaysVisible: true, isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
      ],
      visibleColumns: {}
    };
  },
  computed: {
    isLandlordMode() {
      return this.properties.length > 1 || (this.properties.length === 1 && this.landlord);
    },
    landlordName() {
      return this.landlord?.name || '';
    },
    headerPropertyText() {
      if (this.isLandlordMode) {
        return `All properties under ${this.landlordName}`;
      }
      return this.propertyName || '';
    },
    visibleMonthColumns() {
      return this.monthColumnDefinitions.filter(col => col.alwaysVisible || this.visibleColumns[col.field]);
    },
    logoSrc() {
      return this.base64log ? this.base64log : '';
    },
    sortedGroupedData() {
      if (!this.groupedData.length) return [];
      const data = [...this.groupedData];
      if (this.sortMode === 'unit') {
        data.sort((a, b) => {
          const unitA = a.unit ? parseInt(a.unit, 10) : 999999;
          const unitB = b.unit ? parseInt(b.unit, 10) : 999999;
          return unitA - unitB;
        });
      } else {
        data.sort((a, b) => a.reference.localeCompare(b.reference));
      }
      return data;
    },
    displayedGroupedData() {
      if (!this.selectedUnit) return this.sortedGroupedData;
      return this.sortedGroupedData.filter(group => group.unit === this.selectedUnit);
    }
  },
  watch: {
    properties: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.generateRentRollData();
        }
      },
      deep: true,
      immediate: true
    },
    property(newVal) {
      if (newVal && (!this.properties || this.properties.length === 0)) {
        this.generateRentRollData();
      }
    },
    landlord(newVal) {
      if (newVal && this.properties && this.properties.length > 0) {
        this.generateRentRollData();
      }
    }
  },
  async created() {
    await this.fetchLogoBase64();
  },
  methods: {
    async fetchLogoBase64() {
      try {
        const logosRef = collection(db, 'Logo');
        const q = query(logosRef, where('id', '==', 2));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          this.base64log = snapshot.docs[0].data().logo2 || '';
        }
      } catch (err) {
        console.error('Logo fetch failed:', err);
      }
    },
    getChargeStatusClass(item, field) {
      const charged = Number(item[field] || 0);
      const paid = Number(item[field + 'Paid'] || item[field + 'ChargedPaid'] || 0);

      if (charged === 0) return '';
      if (paid >= charged - 0.01) return 'success--text font-weight-bold';
      if (paid > 0) return 'orange--text font-weight-bold';
      return 'error--text font-weight-bold';
    },
    /**
     * Balance colour logic:
     *   positive balance  → money owed  → RED (or ORANGE if a partial payment was made this month)
     *   zero balance      → paid up     → GREEN
     *   negative balance  → in credit   → GREEN
     *   payment sub-row   → neutral     → BLUE
     */
    getBalanceClass(item) {
      // Payment sub-rows aren't balance cells — don't colour-code them
      if (item.isPayment) return 'primary--text font-weight-bold';

      const balance = Number(item.balance);
      if (isNaN(balance)) return 'error--text font-weight-bold';

      // Money owed
      if (balance > 0.01) {
        const received = Number(item.bankReceived) || 0;
        return received > 0
          ? 'orange--text font-weight-bold'   // partially paid → owing but reducing
          : 'error--text font-weight-bold';    // nothing paid → red
      }

      // Paid up or in credit
      return 'success--text font-weight-bold';
    },
    updateVisibleColumns() {
      if (!this.groupedData.length) {
        this.visibleColumns = {};
        return;
      }
      const newVisible = {};
      this.monthColumnDefinitions.forEach(col => {
        if (col.alwaysVisible) return;
        let hasNonZero = false;
        if (this.chargeColumns.includes(col.field)) {
          hasNonZero = this.groupedData.some(group =>
            group.items.some(item => {
              const charged = Number(item[col.field] || 0);
              const paid = Number(item[col.field + 'Paid'] || item[col.field + 'ChargedPaid'] || 0);
              return charged !== 0 || paid !== 0;
            })
          );
        } else {
          hasNonZero = this.groupedData.some(group =>
            group.items.some(item => Number(item[col.field] || 0) !== 0)
          );
        }
        newVisible[col.field] = hasNonZero;
      });
      this.visibleColumns = newVisible;
    },
    extractUniqueUnits() {
      const units = new Set();
      this.groupedData.forEach(group => {
        if (group.unit) units.add(group.unit);
      });
      this.unitOptions = [
        { text: 'All Units', value: null },
        ...Array.from(units)
          .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
          .map(u => ({ text: `Unit ${u}`, value: u }))
      ];
    },
    async generateRentRollData() {
      try {
        const targetProperties = this.properties.length > 0
          ? this.properties
          : this.property ? [this.property] : [];

        if (targetProperties.length === 0) {
          console.warn("No properties selected for Rent Roll");
          this.groupedData = [];
          return;
        }

        if (this.isLandlordMode) {
          this.landlordName = this.landlord?.name || 'Multiple Landlords';
          this.propertyName = '';
          this.report.title = `${this.landlordName} - Consolidated Rent Roll`;
        } else {
          this.propertyName = targetProperties[0].displayName || 'Unknown Property';
          this.landlordName = '';
          this.report.title = `${this.propertyName} - Rent Roll`;
        }

        const flatData = [];

        for (const prop of targetProperties) {
          let leasesQuery = query(collection(db, "Leases"), where("propertyId", "==", prop.id));
          if (this.unit?.unitName) {
            leasesQuery = query(leasesQuery, where("address", "==", this.unit.unitName));
          }
          const leasesSnapshot = await getDocs(leasesQuery);

          for (const leaseDoc of leasesSnapshot.docs) {
            const leaseData = leaseDoc.data();
            const leaseRef = leaseData.leaseReference;
            const tenantName = leaseData.tenantName || 'Unknown Tenant';
            const unit = leaseData.address?.includes("Unit") ? leaseData.address.split("Unit")[1].trim() : "";
            const txQuery = query(collection(db, "Transactions"), where("leaseReference", "==", leaseRef), orderBy("datePaid", "asc"));
            const txSnapshot = await getDocs(txQuery);

            const monthlyData = {};

            txSnapshot.docs.forEach(doc => {
              const t = doc.data();
              const date = new Date(t.datePaid);
              const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;

              if (!monthlyData[monthYear]) {
                monthlyData[monthYear] = {
                  openingBalance: 0,
                  rentCharged: 0, rentPaid: 0,
                  waterCharged: 0, waterChargedPaid: 0,
                  sewerageCharged: 0, sewerageChargedPaid: 0,
                  sewerageAv: 0, sewerageAvPaid: 0,
                  refuseCharged: 0, refuseChargedPaid: 0,
                  admin: 0, adminPaid: 0,
                  parking: 0, parkingPaid: 0,
                  insurance: 0, insurancePaid: 0,
                  legalExpenses: 0, legalExpensesPaid: 0,
                  repairsMaintenance: 0, repairsMaintenancePaid: 0,
                  utilities: 0, utilitiesPaid: 0,
                  otherVendorExpenses: 0, otherVendorExpensesPaid: 0,
                  otherCharge: 0, otherChargePaid: 0,
                  bankReceived: 0,
                  totalAllocated: 0,
                  paymentDates: [],
                  payments: []
                };
              }

              const amount = parseFloat(t.amountPaid) || 0;
              const allocated = parseFloat(t.allocatedAmount) || 0;
              const desc = (t.description || t.transactionType || '').toLowerCase();

              if (t.type === "payment") {
                monthlyData[monthYear].bankReceived += Math.abs(amount);
                monthlyData[monthYear].paymentDates.push(t.datePaid);

                monthlyData[monthYear].payments.push({
                  date: t.datePaid,
                  amount: Math.abs(amount),
                  allocated: allocated,
                  description: t.description || 'Payment'
                });
              } else if (amount > 0) {
                if (/rent|monthly rent/i.test(desc)) {
                  monthlyData[monthYear].rentCharged += amount;
                  monthlyData[monthYear].rentPaid += allocated;
                } else if (/water/i.test(desc) && !/av|availability/i.test(desc)) {
                  monthlyData[monthYear].waterCharged += amount;
                  monthlyData[monthYear].waterChargedPaid += allocated;
                } else if (/sewerage/i.test(desc) && !/av|availability/i.test(desc)) {
                  monthlyData[monthYear].sewerageCharged += amount;
                  monthlyData[monthYear].sewerageChargedPaid += allocated;
                } else if (/sewerage?\s*av|sewerage?\s*availability/i.test(desc)) {
                  monthlyData[monthYear].sewerageAv += amount;
                  monthlyData[monthYear].sewerageAvPaid += allocated;
                } else if (/refuse/i.test(desc)) {
                  monthlyData[monthYear].refuseCharged += amount;
                  monthlyData[monthYear].refuseChargedPaid += allocated;
                } else if (/admin/i.test(desc)) {
                  monthlyData[monthYear].admin += amount;
                  monthlyData[monthYear].adminPaid += allocated;
                } else if (/parking/i.test(desc)) {
                  monthlyData[monthYear].parking += amount;
                  monthlyData[monthYear].parkingPaid += allocated;
                } else if (/insurance/i.test(desc)) {
                  monthlyData[monthYear].insurance += amount;
                  monthlyData[monthYear].insurancePaid += allocated;
                } else if (/legal/i.test(desc)) {
                  monthlyData[monthYear].legalExpenses += amount;
                  monthlyData[monthYear].legalExpensesPaid += allocated;
                } else if (/repair|maintenance/i.test(desc)) {
                  monthlyData[monthYear].repairsMaintenance += amount;
                  monthlyData[monthYear].repairsMaintenancePaid += allocated;
                } else if (/utilities/i.test(desc)) {
                  monthlyData[monthYear].utilities += amount;
                  monthlyData[monthYear].utilitiesPaid += allocated;
                } else if (/vendor/i.test(desc)) {
                  monthlyData[monthYear].otherVendorExpenses += amount;
                  monthlyData[monthYear].otherVendorExpensesPaid += allocated;
                } else {
                  monthlyData[monthYear].otherCharge += amount;
                  monthlyData[monthYear].otherChargePaid += allocated;
                }
                monthlyData[monthYear].totalAllocated += allocated;
              }
            });

            const sortedMonths = Object.keys(monthlyData).sort((a, b) => {
              const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const [ma, ya] = a.split(' '); const [mb, yb] = b.split(' ');
              return ya - yb || months.indexOf(ma) - months.indexOf(mb);
            });

            for (let i = 0; i < sortedMonths.length; i++) {
              const monthYear = sortedMonths[i];
              const m = monthlyData[monthYear];
              m.openingBalance = i === 0 ? 0 : (monthlyData[sortedMonths[i - 1]]?.closingBalance || 0);
              const currentCharges = m.rentCharged + m.waterCharged + m.sewerageCharged + m.sewerageAv + m.refuseCharged +
                m.admin + m.parking + m.insurance + m.legalExpenses + m.repairsMaintenance + m.utilities + m.otherVendorExpenses + m.otherCharge;
              m.subTotal = m.openingBalance + currentCharges;
              // closingBalance > 0  → money owed
              // closingBalance <= 0 → paid up / in credit
              m.closingBalance = m.subTotal - m.bankReceived;

              const [monthName, year] = monthYear.split(' ');
              const monthStart = new Date(year, new Date(`${monthName} 1, ${year}`).getMonth(), 1);
              const monthEnd = new Date(year, monthStart.getMonth() + 1, 0);
              const rStart = new Date(this.report.parameters.startDate);
              const rEnd = new Date(this.report.parameters.endDate);

              if (monthStart <= rEnd && monthEnd >= rStart) {
                const mainRow = {
                  month: monthYear,
                  reference: leaseRef,
                  tenantName: tenantName,
                  property: prop.displayName,
                  unit: unit,
                  isPayment: false,
                  bcForward: m.openingBalance,
                  rent: m.rentCharged,
                  rentPaid: m.rentPaid,
                  water: m.waterCharged,
                  waterChargedPaid: m.waterChargedPaid,
                  sewerage: m.sewerageCharged,
                  sewerageChargedPaid: m.sewerageChargedPaid,
                  sewerageAv: m.sewerageAv,
                  sewerageAvPaid: m.sewerageAvPaid,
                  refuse: m.refuseCharged,
                  refuseChargedPaid: m.refuseChargedPaid,
                  admin: m.admin,
                  adminPaid: m.adminPaid,
                  parking: m.parking,
                  parkingPaid: m.parkingPaid,
                  insurance: m.insurance,
                  insurancePaid: m.insurancePaid,
                  legalExpenses: m.legalExpenses,
                  legalExpensesPaid: m.legalExpensesPaid,
                  repairsMaintenance: m.repairsMaintenance,
                  repairsMaintenancePaid: m.repairsMaintenancePaid,
                  utilities: m.utilities,
                  utilitiesPaid: m.utilitiesPaid,
                  otherVendorExpenses: m.otherVendorExpenses,
                  otherVendorExpensesPaid: m.otherVendorExpensesPaid,
                  otherCharge: m.otherCharge,
                  otherChargePaid: m.otherChargePaid,
                  subTotal: m.subTotal,
                  paymentDate: m.paymentDates.length > 0 ? new Date(m.paymentDates[0]).toLocaleDateString('en-ZA') : '',
                  bankReceived: m.bankReceived,
                  balance: m.closingBalance,
                  paymentCount: m.payments.length
                };
                flatData.push(mainRow);

                if (m.payments.length > 1) {
                  m.payments.forEach((payment, idx) => {
                    const paymentRow = {
                      month: monthYear,
                      reference: leaseRef,
                      tenantName: tenantName,
                      property: prop.displayName,
                      unit: unit,
                      isPayment: true,
                      paymentNumber: idx + 1,
                      bcForward: 0,
                      rent: 0, rentPaid: 0,
                      water: 0, waterChargedPaid: 0,
                      sewerage: 0, sewerageChargedPaid: 0,
                      sewerageAv: 0, sewerageAvPaid: 0,
                      refuse: 0, refuseChargedPaid: 0,
                      admin: 0, adminPaid: 0,
                      parking: 0, parkingPaid: 0,
                      insurance: 0, insurancePaid: 0,
                      legalExpenses: 0, legalExpensesPaid: 0,
                      repairsMaintenance: 0, repairsMaintenancePaid: 0,
                      utilities: 0, utilitiesPaid: 0,
                      otherVendorExpenses: 0, otherVendorExpensesPaid: 0,
                      otherCharge: 0, otherChargePaid: 0,
                      subTotal: 0,
                      paymentDate: new Date(payment.date).toLocaleDateString('en-ZA'),
                      bankReceived: payment.amount,
                      balance: 0,
                      paymentCount: m.payments.length,
                      paymentAmount: payment.amount,
                      paymentAllocated: payment.allocated,
                      paymentDescription: payment.description
                    };
                    flatData.push(paymentRow);
                  });
                }
              }
            }
          }
        }

        const grouped = [];
        const map = new Map();
        flatData.forEach(item => {
          const key = item.reference;
          if (!map.has(key)) {
            map.set(key, {
              reference: item.reference,
              tenantName: item.tenantName,
              property: item.property,
              unit: item.unit,
              items: []
            });
            grouped.push(map.get(key));
          }
          map.get(key).items.push(item);
        });

        this.groupedData = grouped;
        this.extractUniqueUnits();
        this.updateVisibleColumns();

      } catch (err) {
        console.error("Generate error:", err);
        this.groupedData = [];
      }
    },
    async generatePreview() {
      this.isGeneratingPreview = true;
      try {
        await this.generateRentRollData();
      } catch (error) {
        console.error(error);
      } finally {
        this.isGeneratingPreview = false;
      }
    },
    async generatePdf() {
      if (!this.displayedGroupedData.length) return;
      this.generatingPdf = true;
      await this.$nextTick();

      try {
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 40;
        const usableWidth = pageWidth - 2 * margin;
        const headerReserve = 140;

        const headerEl = document.querySelector('#print-content .report-header');
        const headerCanvas = await html2canvas(headerEl, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        });
        const headerImg = headerCanvas.toDataURL('image/png');
        const headerHeight = (headerCanvas.height * usableWidth) / headerCanvas.width;

        const startNewPage = () => {
          pdf.addPage();
          pdf.addImage(headerImg, 'PNG', margin, margin, usableWidth, headerHeight);
          return margin + headerReserve;
        };

        pdf.addImage(headerImg, 'PNG', margin, margin, usableWidth, headerHeight);
        let currentY = margin + headerReserve;

        for (let i = 0; i < this.displayedGroupedData.length; i++) {
          await this.$nextTick();
          const cardEl = document.querySelectorAll('#print-content .lease-card')[i];
          if (!cardEl) continue;

          const canvas = await html2canvas(cardEl, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false
          });

          const imgWidth = usableWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          let remainingHeight = imgHeight;
          let sourceY = 0;
          let isFirstSliceOfCard = true;

          while (remainingHeight > 0) {
            if (isFirstSliceOfCard) {
              currentY = startNewPage();
              isFirstSliceOfCard = false;
            }

            const availableHeight = pageHeight - currentY - margin;
            const heightThisPage = Math.min(remainingHeight, availableHeight);

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = (heightThisPage / imgHeight) * canvas.height;

            const ctx = sliceCanvas.getContext('2d');
            ctx.drawImage(
              canvas,
              0, sourceY,
              canvas.width, sliceCanvas.height,
              0, 0,
              canvas.width, sliceCanvas.height
            );

            const sliceImg = sliceCanvas.toDataURL('image/png');
            const sliceHeight = (sliceCanvas.height * imgWidth) / canvas.width;

            pdf.addImage(sliceImg, 'PNG', margin, currentY, imgWidth, sliceHeight);

            currentY += sliceHeight;
            remainingHeight -= heightThisPage;
            sourceY += sliceCanvas.height;

            if (remainingHeight > 0) {
              currentY = startNewPage();
            }
          }
        }

        const legendEl = document.querySelector('#print-content .report-legend');
        if (legendEl) {
          const legendCanvas = await html2canvas(legendEl, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
          });
          const legendImg = legendCanvas.toDataURL('image/png');
          const legendHeight = (legendCanvas.height * usableWidth) / legendCanvas.width;

          const requiredSpace = legendHeight + margin;
          const spaceLeft = pageHeight - currentY;

          if (spaceLeft < requiredSpace) {
            currentY = startNewPage();
          }

          pdf.addImage(legendImg, 'PNG', margin, currentY, usableWidth, legendHeight);
        }

        const fileName = `${this.report.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0,10)}.pdf`;
        pdf.save(fileName);

      } catch (error) {
        console.error('PDF generation error:', error);
      } finally {
        this.generatingPdf = false;
      }
    },
    formatCurrency(value) {
      if (value == null || value === '' || isNaN(value)) return '';
      return 'R ' + Number(value).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
};
</script>

<style scoped>
.rent-roll-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem !important;
}

.rent-roll-table th,
.rent-roll-table td {
  border: 1px solid #aaa;
  padding: 4px 6px !important;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rent-roll-table th {
  background-color: #f0f0f0;
  font-weight: bold;
  font-size: 0.75rem !important;
  padding: 4px 4px !important;
  line-height: 1.3;
}

.payment-row {
  background-color: #f5f9ff !important;
}

.payment-row td {
  border-color: #bbdefb !important;
}

.payment-indicator {
  color: #1976d2;
  font-size: 0.7rem;
  font-weight: 600;
}

.lease-header {
  padding: 6px 12px !important;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-bottom: none;
}

.lease-header-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  font-size: 0.8rem;
}

.header-item {
  white-space: nowrap;
}

.lease-card {
  border: 1px solid #ddd;
  margin-bottom: 16px;
}

.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.report-header img {
  max-height: 100px;
  max-width: 100px;
}

.report-header h4 {
  font-size: 1.2rem;
  margin: 4px 0 !important;
}

.report-header p {
  font-size: 0.85rem;
  margin: 2px 0 !important;
}

.legend-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  font-size: 0.8rem;
}

.legend-item {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.legend-square {
  width: 20px;
  height: 20px;
  border: 1px solid #000;
  display: inline-block;
  flex-shrink: 0;
}

.success-bg { background-color: #4CAF50; }
.orange-bg { background-color: #FF8F00; }
.error-bg { background-color: #F44336; }

.success--text { color: #4CAF50 !important; }
.orange--text { color: #FF8F00 !important; }
.error--text { color: #F44336 !important; }
.primary--text { color: #1976D2 !important; }

.v-btn-toggle .v-btn {
  text-transform: none !important;
  font-size: 0.8rem !important;
}

.payment-date {
  font-size: 0.7rem !important;
  display: block;
  max-width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .rent-roll-table { font-size: 0.7rem !important; }
  .rent-roll-table th { font-size: 0.65rem !important; }
  .rent-roll-table td { font-size: 0.65rem !important; }
  .header-item { font-size: 0.7rem; }
  .lease-header-row { gap: 6px 12px; }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-text-field { display: none !important; }
  .v-divider { display: none !important; }
  .rent-roll-table { font-size: 0.65rem !important; }
  .rent-roll-table th,
  .rent-roll-table td { padding: 3px 4px !important; }
  .lease-card { page-break-inside: avoid; }
  .payment-row { background-color: #f5f9ff !important; }
}
</style>